import PropTypes from "prop-types";
import {useEffect, useRef} from "react";
import useGamanzaWidgetHydration from "@/hooks/useGamanzaWidgetHydration";
import useIsMobile from "@/hooks/useIsMobile";
import {useAuthStore} from "@/store/useAuth.store";
import {useIsGamanzaUiEnabled} from "@/utils/gamanzaUi";
import styles from "./GamanzaQuickGamesSidebar.module.css";

const SIDEBAR_WIDGET_ID = "gamanza-quick-games-sidebar";
const STYLE_TAG_ID = "gamanza-quick-games-text-fix";

/** Dark/gray shells (low luminance, low saturation) → transparent; keep purple/gold accents. */
const clearDarkShellBackgrounds = root => {
  if (!root) return;
  root.querySelectorAll("*").forEach(el => {
    if (el.dataset.aygShellCleared === "1") {
      return;
    }
    if (typeof el.className === "string" && el.className.includes("sidebarHeaderMinimalistButton")) {
      return;
    }

    const bg = window.getComputedStyle(el).backgroundColor;
    const match = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (!match) return;

    const r = Number(match[1]);
    const g = Number(match[2]);
    const b = Number(match[3]);
    const a = match[4] === undefined ? 1 : Number(match[4]);
    if (a < 0.05) return;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const sat = max === 0 ? 0 : (max - min) / max;
    const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

    if (lum < 0.4 && sat < 0.28) {
      el.style.setProperty("background", "transparent", "important");
      el.style.setProperty("background-color", "transparent", "important");
      el.style.setProperty("background-image", "none", "important");
      el.style.setProperty("box-shadow", "none", "important");
      el.dataset.aygShellCleared = "1";
    }
  });
};

/**
 * Titles on the purple summary header (e.g. TIENDA) need white in light + dark.
 * lookAndFeel text.primary cannot be white in light mode — it would break body copy.
 * @see https://docs.gamanzaengage.com/docs/frontend-integration/widgets-configuration
 */
const HEADER_TITLE_COLOR = "#ffffff";

const ensureTextFixStyle = () => {
  if (document.getElementById(STYLE_TAG_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_TAG_ID;
  style.textContent = `
    .GamanzaIconButton-root + .GamanzaTypography-root,
    .GamanzaIconButton-root ~ .GamanzaTypography-root,
    [class*="GamanzaIconButton"] + [class*="GamanzaTypography"],
    [class*="GamanzaIconButton"] ~ [class*="GamanzaTypography"] {
      color: ${HEADER_TITLE_COLOR} !important;
    }
  `;
  document.head.appendChild(style);
};

const removeTextFixStyle = () => {
  document.getElementById(STYLE_TAG_ID)?.remove();
};

/** Inline fallback — Emotion sometimes wins on class alone. */
const forceTitleColorsInline = () => {
  document.querySelectorAll('[class*="GamanzaTypography"]').forEach(el => {
    const prev = el.previousElementSibling;
    const prevClass = prev && typeof prev.className === "string" ? prev.className : "";
    const isNextToIconBtn = prevClass.includes("GamanzaIconButton") || prevClass.includes("IconButton");
    if (!isNextToIconBtn) return;
    el.style.setProperty("color", HEADER_TITLE_COLOR, "important");
  });
};

/**
 * Mobile-only Gamanza minimalist sidebar for the Quick Games modal.
 * Placed above the Casino / Sports tab buttons.
 * @see https://docs.gamanzaengage.com/docs/frontend-integration/Gamification/sidebar-widget
 */
const GamanzaQuickGamesSidebar = ({active = false}) => {
  const rootRef = useRef(null);
  const isMobile = useIsMobile({breakpoint: 768});
  const authStatus = useAuthStore(state => state.authStatus);
  const token = useAuthStore(state => state.token);
  const gamanzaUiEnabled = useIsGamanzaUiEnabled();

  const enabled = active && gamanzaUiEnabled && isMobile && authStatus === "authenticated" && Boolean(token);

  const {isReady} = useGamanzaWidgetHydration({
    enabled,
    token,
    rootRef,
    widgetId: SIDEBAR_WIDGET_ID,
  });

  useEffect(() => {
    if (!active) {
      removeTextFixStyle();
      return undefined;
    }
    ensureTextFixStyle();
    forceTitleColorsInline();
    return () => removeTextFixStyle();
  }, [active]);

  useEffect(() => {
    if (!enabled) return undefined;
    const root = document.getElementById(SIDEBAR_WIDGET_ID);
    if (!root) return undefined;

    let debounceId = 0;
    const run = () => {
      clearDarkShellBackgrounds(root);
      ensureTextFixStyle();
      forceTitleColorsInline();
    };
    const scheduleRun = () => {
      window.clearTimeout(debounceId);
      debounceId = window.setTimeout(run, 100);
    };

    run();

    const observer = new MutationObserver(scheduleRun);
    observer.observe(root, {childList: true, subtree: true});

    return () => {
      observer.disconnect();
      window.clearTimeout(debounceId);
    };
  }, [enabled, isReady]);

  if (!enabled) return null;

  return (
    <div
      ref={rootRef}
      className={styles.wrap}
    >
      <div
        id={SIDEBAR_WIDGET_ID}
        className="gamification_widget"
        data-type="sidebar"
        data-height="600px"
        data-minimalist-orientation="portrait"
      />
    </div>
  );
};

GamanzaQuickGamesSidebar.propTypes = {
  active: PropTypes.bool,
};

export default GamanzaQuickGamesSidebar;
